import React, { useEffect, useState } from "react";

import { calculatePrefixForExercise } from "../../../../../../utils/prefix-calculator";
import { DATE_FORMATS } from "../../../../../../constants";
import { getMovementsData } from "../../../../../../utils/exercise-library";
import { SearchDropdown } from "../../../../../../parts/inputs/search-dropdown";
import { SuperSetButton } from "./Buttons";
import { useQuickEditAddSet } from "../../../../../../hooks/useQuickEditAddSet";
import ExerciseContent from "./Content";
import MenuOptionsEditing from "./MenuOptions";
import Notes from "./Notes";
import SvgIcon from "../../../../../../parts/SvgIcon";
import Tooltip from "../../../../../../parts/tooltip-portal";
import { CopyExerciseComponent } from "../../../../../../parts/CopyExerciseComponent";
import { getExerciseName } from "../../../../../../parts/modals/learn-more/blockHelpers";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

const ExerciseEditor = ({
  exercise,
  exercises,
  keyId,
  activeUser,
  athlete,
  editDayMode,
  supersets,
  handleSelectChange,
  selectedExercise,
  dayString,
  athleteId,
  tempExercises,
  handleEditTemporalInput,
  showOptions,
  getTempExercise,
  handleInput,
  addTempSet,
  repMaximums,
  removeSet,
  handleSuperset,
  withWeight,
  fromMetric,
  intensityMeasure,
  isRiR,
  activeUserToRequest,
  date,
  saveEditedExercise,
}) => {
  const [addingNote, setAddingNote] = useState(false);
  const { addingSet, setAddingSet, onAddSet, onDiscardSet, onSubmitSet } =
    useQuickEditAddSet({
      onSubmitCallback: () => {
        saveEditedExercise(athleteId);
      },
    });

  useEffect(() => {
    // Discard the adding note/set states when exiting edit mode
    if (!editDayMode) {
      setAddingNote(false);
      setAddingSet({ state: false, index: undefined });
    }
  }, [editDayMode]);

  useEffect(() => {
    addingNote &&
      document.getElementById(`exercise-${exercise._id}-notes`)?.focus();
  }, [addingNote]);

  const prefix = calculatePrefixForExercise(exercise._id, supersets);
  const movements = getMovementsData(exercise, activeUserToRequest, athlete);
  const someHasRPE = exercise.sets.some((set) => set.rpe);
  const exerciseId = exercise._id;

  const editable = !exercise.sets.some((set) => set.outcomes);
  const selectExercise =
    tempExercises?.find((ex) => ex._id === exerciseId) || exercise;

  const newExercise = getTempExercise(exerciseId);

  const isLast = keyId === exercises.length - 1;

  const isSuperset =
    !isLast && exercise.nextSuperset === exercises[keyId + 1]._id;

  const getTempSets = (arr) => {
    const result = arr.find((ex) => ex._id === exercise._id);
    return result ? result.sets : false;
  };

  const finalSets =
    editDayMode && editable ? getTempSets(tempExercises) : exercise.sets;

  const onAddNote = () => {
    setAddingNote(true);
  };

  const normalizedNotes =
    typeof exercise.notes === "string"
      ? exercise.notes
      : String(exercise.notes || "");

  return (
    <div
      className={`exercisex id_${exerciseId}`}
      key={keyId}
      id={exerciseId}
      data-date={date.format(DATE_DAY_FORMAT)}
    >
      <div
        className={`titlex exercise-element-title${
          editDayMode && editable
            ? " editing"
            : editDayMode
            ? " not-editable"
            : ""
        }`}
      >
        <div className="title-content">
          <span className="calendar-prefix">{prefix}</span>
          <div
            className={`name-order${
              !(
                !editDayMode &&
                editable &&
                showOptions &&
                selectedExercise === exerciseId
              )
                ? " editing"
                : ""
            }`}
          >
            {editDayMode ? (
              editable ? (
                <SearchDropdown
                  options={movements}
                  placeholder={getExerciseName(selectExercise)}
                  valueKey="movementLabel"
                  handleChange={(val) => handleSelectChange(val, exerciseId)}
                />
              ) : (
                <Tooltip body="This exercise has already been completed, cannot edit">
                  <p>{getExerciseName(exercise)}</p>
                </Tooltip>
              )
            ) : (
              <p>{getExerciseName(exercise)}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editable && (
            <>
              <CopyExerciseComponent
                classNames="!p-0"
                exercises={exercise}
                athlete={athlete}
              />
              <img
                src="/img/new-icons/drag-outline.svg"
                className="grabber cursor-grab"
                alt="drag"
              />
            </>
          )}
          <MenuOptionsEditing
            exercises={exercises}
            exerciseId={exerciseId}
            dayString={dayString}
            athleteId={athleteId}
            shown={!editDayMode && editable}
          />
        </div>
      </div>
      <ExerciseContent
        exercise={exercise}
        someHasRPE={someHasRPE}
        finalSets={finalSets}
        newExercise={newExercise}
        editDayMode={editDayMode}
        editable={editable}
        handleEditTemporalInput={handleEditTemporalInput}
        handleInput={handleInput}
        addTempSet={addTempSet}
        removeSet={removeSet}
        repMaximums={repMaximums}
        withWeight={withWeight}
        fromMetric={fromMetric}
        intensityMeasure={intensityMeasure}
        isRiR={isRiR}
        units={activeUser.units}
        addingSet={addingSet}
        onAddSet={onAddSet}
        onSubmitSet={onSubmitSet}
        onDiscardSet={onDiscardSet}
      />
      {(normalizedNotes.length > 0 || addingNote) && (
        <Notes
          {...{
            exerciseNotes: normalizedNotes,
            exerciseId,
            editDayMode,
            editable,
            handleInput,
          }}
        />
      )}
      <div role="supersets-notes-buttons">
        <SuperSetButton
          {...{
            editDayMode,
            editable,
            isLast,
            isSuperset,
            handleSuperset,
            exercise,
            exercises,
            keyId,
          }}
        />
        {normalizedNotes.length <= 0 && editDayMode && !addingNote && (
          <button
            data-button-type="text"
            onClick={() => {
              onAddNote();
            }}
          >
            <SvgIcon src="/img/new-icons/note-gradient.svg" alt="add notes" />
            <span>Add Note</span>
          </button>
        )}
        {normalizedNotes.length > 0 && editDayMode && (
          <button
            data-button-type="text-white"
            data-action="cancel"
            onClick={() => {
              setAddingNote(false);
              handleInput({
                value: "",
                field: "notes",
                exerciseId,
              });
            }}
          >
            <img src={"/img/new-icons/red-trash.svg"} width={16} height={16} />
            <span>Remove Note</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ExerciseEditor;
